import { useCallback, useEffect, useState } from "react";
import {
  suggestInterpretationsForDeviation,
  suggestPatternsForDeviation,
} from "../api";
/** 
 * Hook for the DeviationItem component.
 * @param deviation - The deviation object.
 * @param onSaveDeviation - The function to save the deviation.
 * @param onUpdateDiagnosticsCenter - The function to update the diagnostics center.

 * @returns {
  isEditing: boolean, // Whether the deviation is being edited
  editData: object, // The edit data
  diagnosticMatches: object, // The diagnostic matches
  interpretationMatches: object, // The interpretations matches
  isLoadingDiagnosticsMatches: boolean, // Whether the diagnostics matches are loading
  isLoadingInterpretationsMatches: boolean, // Whether the interpretations matches are loading
  handleEdit: function, // The function to edit the deviation
  handleCancelEdit: function, // The function to cancel the edit
  handleInputChange: function, // The function to change the input
  handleSave: function // The function to save the deviation
}

*/
export const useDeviationItem = (
  deviation,
  onSaveDeviation,
  onUpdateDiagnosticsCenter,
  fetchOnEdit = true
) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState(null);
  const [diagnosticMatches, setDiagnosticMatches] = useState(null);
  const [interpretationMatches, setInterpretationMatches] = useState(null);
  const [isLoadingDiagnosticsMatches, setIsLoadingDiagnosticsMatches] =
    useState(false);
  const [isLoadingInterpretationsMatches, setIsLoadingInterpretationsMatches] =
    useState(false);

  useEffect(() => {
    if (isEditing && deviation) {
      setEditData({
        status: deviation.status,
        clinician_interpretation: deviation.clinician_interpretation || "",
        diagnosis_category: deviation.diagnosis_category || "",
      });
    } else {
      setIsEditing(false);
      setEditData(null);
    }
  }, [deviation, isEditing]);

  const handleEdit = () => {
    setIsEditing(true);
    setEditData({
      status: deviation.status,
      clinician_interpretation: deviation.clinician_interpretation || "",
      diagnosis_category: deviation.diagnosis_category || "",
    });
    fetchOnEdit && fetchDiagnosticsMatches() && fetchInterpretationMatches();
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditData(null);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setEditData((prev) => ({ ...prev, [name]: value }));
  };

  const handleInputData = (editData) => {
    setEditData((prev) => ({ ...prev, ...editData }));
  };

  const handleSave = async () => {
    if (!editData) return;
    try {
      await onSaveDeviation(deviation.id, editData);
      if (editData.status === "diagnosed" && editData.diagnosis_category) {
        await onUpdateDiagnosticsCenter(deviation.id);
      }
      setIsEditing(false);
    } catch (error) {
      console.error("Save failed in DeviationItem", error);
    }
  };

  const fetchDiagnosticsMatches = useCallback(async () => {
    if (!deviation) return;
    setIsLoadingDiagnosticsMatches(true);
    try {
      const response = await suggestPatternsForDeviation(deviation.id);
      setDiagnosticMatches(response.data);
    } catch (err) {
      console.error("Error fetching diagnostics matches:", deviation.id, err);
      setDiagnosticMatches({ message: "Error fetching diagnostics matches." });
    }
    setIsLoadingDiagnosticsMatches(false);
  }, [deviation]);

  const fetchInterpretationMatches = useCallback(async () => {
    if (!deviation) return;
    setIsLoadingInterpretationsMatches(true);
    try {
      const response = await suggestInterpretationsForDeviation(deviation.id);
      setInterpretationMatches(response.data);
    } catch (err) {
      console.error(
        "Error fetching interpretation matches:",
        deviation.id,
        err
      );
      setInterpretationMatches({
        message: "Error fetching interpretation matches.",
      });
    }
    setIsLoadingInterpretationsMatches(false);
  }, [deviation]);

  return {
    isEditing,
    editData,
    diagnosticMatches,
    interpretationMatches,
    isLoadingDiagnosticsMatches,
    isLoadingInterpretationsMatches,
    handleEdit,
    handleCancelEdit,
    handleInputChange,
    handleSave,
    handleInputData,
    fetchDiagnosticsMatches,
    fetchInterpretationMatches,
  };
};
